import React from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';

import {
  LEARNING_CENTER_PRICE_PATH,
  PAYMENT_PROTECTION_POLICY_PATH
} from 'dpl/shared/constants/urls';
import SmartLink from 'dpl/components/SmartLink';
import Drawer, {
  DrawerItem,
  DrawerItemContent,
  DrawerItemTitle
} from 'dpl/common/components/Drawer';
import SmartImage from 'dpl/components/SmartImage';
import { isMobileUA } from 'dpl/shared/utils';

import buyerGuideDesktop from 'dpl/assets/buyer_guide_desktop.png';
import buyerGuideMobile from 'dpl/assets/buyer_guide_mobile.svg';

const IS_MOBILE_UA = isMobileUA();

export default function BuyersGuideModule() {
  const { breedName, buyersGuidePriceRange } = useSelector(
    state => state.server
  );

  const buyersGuideQA = [
    {
      question: `What’s the expected price for a ${breedName}?`,
      answer: (
        <>
          Nationally, prices usually range from{' '}
          {buyersGuidePriceRange.low_price} to{' '}
          {buyersGuidePriceRange.high_price}. While it may cost more up front,
          starting with a healthy puppy can help you avoid costly issues later.
          These ranges reflect the care behind responsible breeding and offer a
          reliable view of{' '}
          <SmartLink
            className="underline"
            to={LEARNING_CENTER_PRICE_PATH}
            target="_blank"
          >
            what ethical breeding really costs
          </SmartLink>
          .
        </>
      )
    },
    {
      question: 'How do I know if a breeder is ethical?',
      answer:
        'Ethical breeders put their dogs’ well-being first. They raise puppies in clean, safe environments, are thoughtful about how and when they breed, and stay involved after the dog goes home. They care about health and behavior, not just looks, and they want their puppies placed in the right homes. At Good Dog, we screen every breeder against expert-backed standards so you can feel confident you’re starting in the right place.'
    },
    {
      question: 'What questions should I ask my breeder?',
      answer:
        'Ask how the puppies are raised, what the parents are like, and what steps the breeder takes to prevent health issues. A responsible breeder should be able to walk you through their approach to pairing dogs, any health testing they’ve done, and the care their dogs receive. You can also ask about temperament, socialization, early training, and how they support you after pickup.'
    },
    {
      question: 'What if my breeder isn’t nearby?',
      answer:
        'Most people end up driving a few hours or across state lines to work with the right breeder. It’s more important to find a good fit than a nearby one. Many breeders offer virtual meet-and-greets and regular photo updates, so you can still build a strong connection before pickup day.'
    },
    {
      question: 'How does Good Dog keep me protected?',
      answer: (
        <>
          Every breeder on Good Dog is screened against expert-backed standards,
          and only about 1 in 5 who apply are accepted. We protect your payments
          through{' '}
          <SmartLink
            className="underline"
            to={PAYMENT_PROTECTION_POLICY_PATH}
            target="_blank"
          >
            Good Dog Payment Protection
          </SmartLink>
          , monitor for issues, and step in if something goes wrong. From first
          message to pickup day, we’ve built safety into every part of the
          process so you’re never on your own.
        </>
      )
    }
  ];

  return (
    <div className="BuyersGuideModule container-xxl pv10 pv16-md">
      <div className="flex flex-column flex-md-row gap-x-48">
        <div className="flex-1 mb4 mb0-md">
          <div className="flex gap-x-16">
            {IS_MOBILE_UA && (
              <SmartImage
                src={buyerGuideMobile}
                loadingClass="bg-light-gray"
                width="75px"
                lazy
              />
            )}
            <div>
              <p className="font-16 md:font-18 fw-medium secondary lh-body mb1-md">
                {breedName}
              </p>
              <h5 className="font-32 md:font-48 lh-title">Buyer’s Guide</h5>
            </div>
          </div>
          {!IS_MOBILE_UA && (
            <SmartImage
              wrapperOnlyClassName="mt8"
              src={buyerGuideDesktop}
              loadingClass="bg-light-gray"
              width="160px"
              lazy
            />
          )}
        </div>
        <div className="flex-1">
          {buyersGuideQA.map((qa, idx) => (
            <Drawer key={idx} initialOpenKey={idx === 0 ? `qa-${idx}` : null}>
              <DrawerItem
                itemKey={`qa-${idx}`}
                className={classnames('bt', {
                  'b--default': !IS_MOBILE_UA,
                  'b--gray-200': IS_MOBILE_UA
                })}
              >
                <DrawerItemTitle hideIcon>
                  <p className="font-16 md:font-18 fw-medium lh-body">
                    {qa.question}
                  </p>
                </DrawerItemTitle>
                <DrawerItemContent className="nt3 pb4">
                  <p className="font-16 md:font-18 secondary o-90 lh-body">
                    {qa.answer}
                  </p>
                </DrawerItemContent>
              </DrawerItem>
            </Drawer>
          ))}
        </div>
      </div>
    </div>
  );
}
